/**
 * Crash capture for every Vello app (W8-DRIVER-SHIFT-TRUTH, D442; finding X-08 — "no app has an ErrorBoundary or crash
 * capture": a render error blanked the whole screen and an uncaught error was lost).
 *
 * Framework-neutral pieces the React boundary (`error-boundary.ts`, and `@vello/web-theme` on the web) and the global
 * handlers share:
 *  - {@link describeCrash} turns anything thrown into a bounded, redacted report (a message and a stack are cut to a fixed
 *    length; e-mail addresses and long digit runs — phone numbers, document numbers — are masked, because an error message
 *    may quote user input);
 *  - {@link createCrashLog} keeps the last reports in memory and hands each to a sink (the console by default). A server-
 *    side sink is `crash-upload.ts` (D691, gap 12) - it is just another sink;
 *  - {@link installGlobalErrorHandler} (React Native's `ErrorUtils`) and {@link installWindowErrorHandlers} (the browser's
 *    `error` / `unhandledrejection`) record what no boundary sees, chaining any handler already installed.
 */
import { CRASH_LIMITS, redactCrashText } from '@vello/api-client';
const MAX_MESSAGE = CRASH_LIMITS.message;
const MAX_STACK = CRASH_LIMITS.stack;
// D691: the masking (and the size limits) are defined once, in `@vello/api-client`, so the phone and the API cut and mask the same way.
export { redactCrashText };
const cut = (text, max) => (text.length > max ? `${text.slice(0, max)}…` : text);
/** A bounded, redacted report of anything thrown. */
export function describeCrash(error, extra) {
    const e = error instanceof Error ? error : undefined;
    const rawMessage = e !== undefined ? e.message : typeof error === 'string' ? error : (() => {
        try {
            return JSON.stringify(error) ?? String(error);
        }
        catch {
            return String(error);
        }
    })();
    return {
        kind: extra.kind,
        message: cut(redactCrashText(rawMessage), MAX_MESSAGE),
        ...(e?.name !== undefined ? { name: e.name } : {}),
        ...(e?.stack !== undefined ? { stack: cut(redactCrashText(e.stack), MAX_STACK) } : {}),
        ...(extra.componentStack !== undefined ? { componentStack: cut(extra.componentStack, MAX_STACK) } : {}),
        ...(extra.fatal !== undefined ? { fatal: extra.fatal } : {}),
        ...(extra.app !== undefined ? { app: extra.app } : {}),
        at: extra.at ?? new Date().toISOString(),
    };
}
/** The default sink: the console (the platform's own log — logcat on Android, the browser console on the web). */
export const consoleCrashSink = (report) => {
    // A crash is exactly what a developer must see; nothing identifying survives `describeCrash`.
    console.error(`[vello:crash] ${report.kind}${report.app !== undefined ? ` ${report.app}` : ''}: ${report.message}`);
};
export function createCrashLog(opts = {}) {
    const capacity = Math.max(1, opts.capacity ?? 20);
    const sink = opts.sink ?? consoleCrashSink;
    const ring = [];
    return {
        record: (report) => {
            ring.push(report);
            if (ring.length > capacity)
                ring.splice(0, ring.length - capacity);
            try {
                sink(report);
            }
            catch {
                /* a sink never turns a crash into a second crash */
            }
        },
        recent: () => [...ring],
    };
}
/** Record every uncaught error, then hand it to the handler that was installed before (the red box, the native crash). */
export function installGlobalErrorHandler(errorUtils, reporter, app) {
    const previous = errorUtils.getGlobalHandler();
    const handler = (error, isFatal) => {
        reporter.record(describeCrash(error, { kind: 'global', ...(isFatal !== undefined ? { fatal: isFatal } : {}), ...(app !== undefined ? { app } : {}) }));
        previous?.(error, isFatal);
    };
    errorUtils.setGlobalHandler(handler);
    return () => {
        if (previous !== undefined)
            errorUtils.setGlobalHandler(previous);
    };
}
/** Record the browser's uncaught errors and unhandled rejections (the page keeps its default behaviour). */
export function installWindowErrorHandlers(target, reporter, app) {
    const onError = (event) => {
        const e = event;
        reporter.record(describeCrash(e?.error ?? e?.message ?? event, { kind: 'global', ...(app !== undefined ? { app } : {}) }));
    };
    const onRejection = (event) => {
        reporter.record(describeCrash(event?.reason ?? event, { kind: 'unhandled-rejection', ...(app !== undefined ? { app } : {}) }));
    };
    target.addEventListener('error', onError);
    target.addEventListener('unhandledrejection', onRejection);
    return () => {
        target.removeEventListener('error', onError);
        target.removeEventListener('unhandledrejection', onRejection);
    };
}
//# sourceMappingURL=crash-report.js.map